import { injectable } from "@launchtray/tsyringe-async";
import { PlayerModel } from "./PlayerModel";
import { LevelConfiguration, SpawnConfiguration } from "../configs/LevelConfiguration";
import { LevelsResourcesPackage } from "../../localization/LevelsResourcesPackage";
import { nameof } from "bindable-data";
import { ObjectFieldsChecker } from "../../ObjectFieldsChecker";
import { GameElement } from "../../scenes/game/models/field/GameElement";
import { ISpawnerConfiguration } from "../../scenes/game/components/3d/spawner/ISpawnerConfiguration";
import { GameViewModel } from "../../scenes/game/models/GameViewModel";
import { isMobile } from "pixi.js";

@injectable()
export class LevelModel {
    private static readonly LEVEL_RESOURCE_FORMAT = "assets/levels/level_{0}.json";

    private levelConfigurationValue: LevelData;

    public get levelConfiguration(): LevelData {
        return this.levelConfigurationValue;
    }

    public constructor(
        private readonly levelsResources: LevelsResourcesPackage,
        private readonly playerModel: PlayerModel
    ) {
        this.levelConfigurationValue = this.parseLevelData(this.playerModel.currentLevel.value);
        this.playerModel.propertyChanged.add(this.onPlayerModelPropertyChange, this);
    }

    private onPlayerModelPropertyChange(model: PlayerModel, key: string): void {
        if (key != nameof(model, "currentLevel")) {
            return;
        }
        this.levelConfigurationValue = this.parseLevelData(model.currentLevel.value);
    }

    private parseLevelData(level: number): LevelData {
        const levelResourceKey = LevelModel.LEVEL_RESOURCE_FORMAT.replace("{0}", level.toString());
        const levelConfig = this.levelsResources.resource<LevelConfiguration>(levelResourceKey);
        if (levelConfig == null) {
            return this.generateLevelConfiguration(level);
        }
        try {
            return this.parseLevelConfiguration(levelConfig);
        }
        catch (error) {
            console.error(`Unable to parse level config ${level}: `, error);
            return this.generateLevelConfiguration(level);
        }
    }

    private generateLevelConfiguration(level: number): LevelData {
        const difficulty = this.getDifficulty(level);
        const timeToFirstMoleMs = 1000;
        const pattern = level % 3;
        switch (pattern) {
            case 0: {
                const time = (30 + difficulty * 15) * 1000;
                const vegetables = 10 - difficulty * 2;
                const spawnersCount = 6 + difficulty * 2;
                const commonSpawnConfiguration: SpawnConfiguration =
                {
                    spawnChance: 0.20 + 0.15 * difficulty,
                    spawnIntervalMS: 4000 - 350 * difficulty,
                    generalSpawnConfig: [
                        { gameElement: GameElement.Portal, chance: 0.20 },
                        { gameElement: GameElement.AttackingMole, chance: 0.05 },
                        { gameElement: GameElement.EatingMole, chance: 0.45 },
                        { gameElement: GameElement.AggressiveEaterMole, chance: 0.15 },
                        { gameElement: GameElement.BoosterBox, chance: 0.03 },
                        { gameElement: GameElement.HealBox, chance: 0.07 },
                        { gameElement: GameElement.WeaponsBox, chance: 0.05 },
                    ],
                    presetSpawnableItems: []
                };
                console.log("Generate level with Portal pattern: \n", {
                    difficulty,
                    time,
                    vegetables,
                    spawnersCount,
                    spawnChance: commonSpawnConfiguration.spawnChance,
                    spawnIntervalMS: commonSpawnConfiguration.spawnIntervalMS
                });
                return this.parseLevelConfiguration({
                    time,
                    timeToFirstMoleMs,
                    vegetables,
                    spawnersCount,
                    commonSpawnConfiguration,
                    coinsReward: 100
                });
            }
            case 1: {
                const time = (45 + difficulty * 15) * 1000;
                const vegetables = 10 - difficulty * 2;
                const spawnersCount = 6 + difficulty * 2;
                const commonSpawnConfiguration: SpawnConfiguration =
                {
                    spawnChance: 0.35 + 0.1 * difficulty,
                    spawnIntervalMS: 4100 - 400 * difficulty,
                    generalSpawnConfig: [
                        { gameElement: GameElement.Portal, chance: 0.10 },
                        { gameElement: GameElement.AttackingMole, chance: 0.05 },
                        { gameElement: GameElement.EatingMole, chance: 0.55 },
                        { gameElement: GameElement.AttackingMole, chance: 0.15 },
                        { gameElement: GameElement.BoosterBox, chance: 0.03 },
                        { gameElement: GameElement.HealBox, chance: 0.07 },
                        { gameElement: GameElement.WeaponsBox, chance: 0.05 },
                    ],
                    presetSpawnableItems: []
                };
                console.log("Generate level with Timer pattern: \n", {
                    difficulty,
                    time,
                    vegetables,
                    spawnersCount,
                    spawnChance: commonSpawnConfiguration.spawnChance,
                    spawnIntervalMS: commonSpawnConfiguration.spawnIntervalMS
                });
                return this.parseLevelConfiguration({
                    time,
                    timeToFirstMoleMs,
                    vegetables,
                    spawnersCount,
                    commonSpawnConfiguration,
                    coinsReward: 100
                });
            }
            case 2: {
                const time = (30 + difficulty * 10) * 1000;
                const vegetables = 6 - difficulty;
                const spawnersCount = 6 + 2 * difficulty;
                const commonSpawnConfiguration: SpawnConfiguration =
                {
                    spawnChance: 0.25 + 0.2 * difficulty,
                    spawnIntervalMS: 4000 - 500 * difficulty,
                    generalSpawnConfig: [
                        { gameElement: GameElement.Portal, chance: 0.15 },
                        { gameElement: GameElement.AttackingMole, chance: 0.10 },
                        { gameElement: GameElement.EatingMole, chance: 0.40 },
                        { gameElement: GameElement.AggressiveEaterMole, chance: 0.20 },
                        { gameElement: GameElement.BoosterBox, chance: 0.03 },
                        { gameElement: GameElement.HealBox, chance: 0.07 },
                        { gameElement: GameElement.WeaponsBox, chance: 0.05 },
                    ],
                    presetSpawnableItems: []
                };
                console.log("Generate level with Vegetables pattern: \n", {
                    difficulty,
                    time,
                    vegetables,
                    spawnersCount,
                    spawnChance: commonSpawnConfiguration.spawnChance,
                    spawnIntervalMS: commonSpawnConfiguration.spawnIntervalMS
                });
                return this.parseLevelConfiguration({
                    time,
                    timeToFirstMoleMs,
                    vegetables,
                    spawnersCount,
                    commonSpawnConfiguration,
                    coinsReward: 100
                });
            }
        }
    }

    // "Difficulty" for level from easy(1) to hard(3)
    private getDifficulty(level: number): number {
        if (level < 10) {
            return 1;
        }
        else if (level < 30) {
            const rand = Math.random();
            if (rand < 0.5) {
                return 1;
            } else if (rand < 0.9) {
                return 2;
            } else {
                return 3;
            }
        }
        else {
            const rand = Math.random();
            if (rand < 0.3) {
                return 1;
            } else if (rand < 0.7) {
                return 2;
            } else {
                return 3;
            }
        }
    }

    private parseLevelConfiguration(config: LevelConfiguration): LevelData {
        let spawnersConfiguration: Array<SpawnerData> = [];
        ObjectFieldsChecker.throwIfInvalidFields(
            config,
            [
                "time",
                "timeToFirstMoleMs",
                "vegetables",
                "coinsReward",
                "commonSpawnConfiguration",
            ],
            "levelConfig"
        );

        const spawnersCount = Math.max(config.spawnersCount ?? 0, config.spawnersConfiguration?.length ?? 0);
        if (spawnersCount < config.vegetables) {
            throw new Error("Spawners count less than vegetables");
        }

        const freeSpawnerIndices = new Array<number>(GameViewModel.GAME_FIELD_WIDTH * GameViewModel.GAME_FIELD_HEIGHT).fill(0).map((_value, index) => index);
        // Configured by spawners array
        if (config.spawnersConfiguration != null && config.spawnersConfiguration.length > 0) {
            let vegetables = config.vegetables;
            for (const spawnerConfig of config.spawnersConfiguration) {
                ObjectFieldsChecker.throwIfInvalidFields(
                    spawnerConfig,
                    [
                        "index",
                        "mobileIndex",
                        "spawnerConfiguration"
                    ],
                    "spawnerConfig"
                );
                ObjectFieldsChecker.throwIfInvalidFields(
                    spawnerConfig.spawnerConfiguration,
                    ["withVegetable"],
                    "spawnerConfig:spawnerConfiguration"
                );
                if (spawnerConfig.spawnerConfiguration.withVegetable) {
                    vegetables--;
                }
                const freeSpawnerIndex = freeSpawnerIndices.indexOf(spawnerConfig.index);
                if (freeSpawnerIndex == -1) {
                    throw new Error(`Invalid index of spawner: ${spawnerConfig.index} (out of bounds or already configured)`);
                }
                freeSpawnerIndices.splice(freeSpawnerIndex, 1);

                spawnersConfiguration.push({
                    index: isMobile.phone ? spawnerConfig.mobileIndex : spawnerConfig.index,
                    spawnerConfiguration: {
                        ...config.commonSpawnConfiguration,
                        ...spawnerConfig.spawnerConfiguration
                    }
                });
            }
            if (config.spawnersConfiguration.length < spawnersCount) {
                // Generate additional spawners
                this.generateSpawnersData(freeSpawnerIndices, spawnersConfiguration, config.commonSpawnConfiguration, spawnersCount - config.spawnersConfiguration.length, vegetables);
            }
        }
        // Spawners array & spawnersCount not configured
        else if (config.spawnersCount == null) {
            throw new Error("Spawners not configured, field `spawnerConfigurations` or `spawnersCount` not defined");
        }
        // Configured by spawners count
        else {
            this.generateSpawnersData(freeSpawnerIndices, spawnersConfiguration, config.commonSpawnConfiguration, spawnersCount, config.vegetables);
        }
        const finalVegetablesCount = spawnersConfiguration.filter(spawner => spawner.spawnerConfiguration.withVegetable).length;
        return {
            coinsReward: config.coinsReward,
            time: config.time,
            timeToFirstMole: config.timeToFirstMoleMs,
            vegetables: finalVegetablesCount,
            characterHealth: config.characterHealth ?? 100,
            spawnersConfiguration
        };
    }

    // private generateSpawnersData(freeSpawnerIndices: Array<number>, spawnConfiguration: SpawnConfiguration, count: number, vegetables: number): Array<SpawnerData> {
    //     const result: Array<SpawnerData> = [];
    //     for (let i = 0; i < count; i++) {
    //         const freeIndex = Math.floor(Math.random() * freeSpawnerIndices.length);
    //         const index = freeSpawnerIndices[freeIndex];
    //         freeSpawnerIndices.splice(freeIndex, 1);
    //         result.push({
    //             index,
    //             spawnerConfiguration: {
    //                 ...spawnConfiguration,
    //                 withVegetable: vegetables-- <= 0
    //             }
    //         });
    //     }
    //     return result;
    // }

    private generateSpawnersData(/*out*/freeSpawnerIndices: Array<number>, /*out*/spawnersData: Array<SpawnerData>, spawnConfiguration: SpawnConfiguration, count: number, vegetables: number): void {
        // Find busy indices & remove area around to avoid intersections
        for (const spawnerData of spawnersData) {
            const spawnConfig = spawnerData.spawnerConfiguration.generalSpawnConfig;
            const withPortalChances = spawnConfig.findIndex(({ gameElement }) => gameElement == GameElement.Portal) != -1;
            this.removeIndicesInRange(freeSpawnerIndices, spawnerData.index, withPortalChances ? 1 : 0);
        }
        const withPortalChances = spawnConfiguration.generalSpawnConfig.findIndex(({ gameElement }) => gameElement == GameElement.Portal) != -1;
        for (let i = 0; i < count; i++) {
            if (freeSpawnerIndices.length == 0) {
                console.warn(`Unable to generate ${count} spawners, not enough free points without elements intersections.\nNumber of vegetables that did not fit: ${Math.max(vegetables, 0)}\nTotal spawners: ${spawnersData.length}`);
                break;
            }
            const index = freeSpawnerIndices[Math.floor(Math.random() * freeSpawnerIndices.length)];
            this.removeIndicesInRange(freeSpawnerIndices, index, withPortalChances ? 1 : 0);
            spawnersData.push({
                index,
                spawnerConfiguration: {
                    ...spawnConfiguration,
                    withVegetable: --vegetables >= 0
                }
            });
        }
    }

    private removeIndicesInRange(outIndices: Array<number>, index: number, range: number): void {
        const centerX = index % GameViewModel.GAME_FIELD_WIDTH;
        const centerY = Math.floor(index / GameViewModel.GAME_FIELD_WIDTH);

        for (let dy = -range; dy <= range; dy++) {
            const y = centerY + dy;
            if (y < 0 || y >= GameViewModel.GAME_FIELD_HEIGHT) {
                continue;
            }

            for (let dx = -range; dx <= range; dx++) {
                const x = centerX + dx;
                if (x < 0 || x >= GameViewModel.GAME_FIELD_WIDTH) {
                    continue;
                }

                const index = y * GameViewModel.GAME_FIELD_WIDTH + x;
                const arrIndex = outIndices.indexOf(index);
                if (arrIndex == -1) {
                    continue;
                }
                outIndices.splice(arrIndex, 1);
            }
        }
        const targetArrIndex = outIndices.indexOf(index);
        outIndices.splice(targetArrIndex, 1);
    }

    public destroy(): void {
        this.playerModel.propertyChanged.remove(this.onPlayerModelPropertyChange, this);
    }
}

type LevelData = {
    time: number;
    timeToFirstMole: number;
    vegetables: number;
    characterHealth: number;
    coinsReward: number;
    spawnersConfiguration: Array<SpawnerData>
};

type SpawnerData = {
    index: number;
    spawnerConfiguration: ISpawnerConfiguration
}
